/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {ApplicationRef} from '../application_ref';
import {ChangeDetectorRef} from '../change_detection/change_detector_ref';
/**
 * \@stable
 * @abstract
 */
export abstract class ViewRef extends ChangeDetectorRef {
/**
 * Destroys the view and all of the data structures associated with it.
 * @abstract
 * @return {?}
 */
destroy() {}
/**
 * @abstract
 * @return {?}
 */
destroyed() {}
/**
 * @abstract
 * @param {?} callback
 * @return {?}
 */
onDestroy(callback: Function) {}
}
/**
 * Represents an Angular View.
 * 
 * <!-- TODO: move the next two paragraphs to the dev guide -->
 * A View is a fundamental building block of the application UI. It is the smallest grouping of
 * Elements which are created and destroyed together.
 * 
 * Properties of elements in a View can change, but the structure (number and order) of elements in
 * a View cannot. Changing the structure of Elements can only be done by inserting, moving or
 * removing nested Views via a {\@link ViewContainerRef}. Each View can contain many View Containers.
 * <!-- /TODO -->
 * 
 * ### Example
 * 
 * Given this template...
 * 
 * ```
 * Count: {{items.length}}
 * <ul>
 *   <li *ngFor="let  item of items">{{item}}</li>
 * </ul>
 * ```
 * 
 * We have two {\@link TemplateRef}s:
 * 
 * Outer {\@link TemplateRef}:
 * ```
 * Count: {{items.length}}
 * <ul>
 *   <ng-template ngFor let-item [ngForOf]="items"></ng-template>
 * </ul>
 * ```
 * 
 * Inner {\@link TemplateRef}:
 * ```
 *   <li>{{item}}</li>
 * ```
 * 
 * Notice that the original template is broken down into two separate {\@link TemplateRef}s.
 * 
 * The outer/inner {\@link TemplateRef}s are then assembled into views like so:
 * 
 * ```
 * <!-- ViewRef: outer-0 -->
 * Count: 2
 * <ul>
 *   <ng-template view-container-ref></ng-template>
 *   <!-- ViewRef: inner-1 --><li>first</li><!-- /ViewRef: inner-1 -->
 *   <!-- ViewRef: inner-2 --><li>second</li><!-- /ViewRef: inner-2 -->
 * </ul>
 * <!-- /ViewRef: outer-0 -->
 * ```
 * \@experimental
 * @abstract
 */
export abstract class EmbeddedViewRef<C> extends ViewRef {
/**
 * @abstract
 * @return {?}
 */
context() {}
/**
 * @abstract
 * @return {?}
 */
rootNodes() {}
}

export interface InternalViewRef extends ViewRef {
  detachFromAppRef(): void;
  attachToAppRef(appRef: ApplicationRef): void;
}



// WEBPACK FOOTER //
// ../../../../packages/core/src/linker/view_ref.ts